import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, FileText, Calendar, DollarSign, ArrowRight, Home } from 'lucide-react';

const SubmissionSuccess = () => {
  const { submissionId } = useParams();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Application Submitted Successfully!
          </h1>
          <p className="text-lg text-gray-600">
            Thank you for your submission. Your application has been received and is now under review.
          </p>
        </div>

        {/* Success Details */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            What Happens Next?
          </h2>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Application Review</h3>
                <p className="text-gray-600 text-sm">
                  Our selection committee will review your application and portfolio. This process typically takes 2-4 weeks.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Status Updates</h3>
                <p className="text-gray-600 text-sm">
                  You'll receive email notifications about your application status. You can also check your status in your artist dashboard.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Final Decision</h3>
                <p className="text-gray-600 text-sm">
                  You'll be notified of the final decision via email. If accepted, you'll receive further instructions for participation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="font-medium text-blue-900 mb-4">Important Information</h3>
          <div className="space-y-3 text-sm text-blue-700">
            <div className="flex items-start space-x-2">
              <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Your application has been saved and cannot be modified after submission.</p>
            </div>
            <div className="flex items-start space-x-2">
              <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Please allow 2-4 weeks for the review process to complete.</p>
            </div>
            <div className="flex items-start space-x-2">
              <DollarSign className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Your submission fee has been processed and is non-refundable.</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/artist/submissions"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
          >
            View My Applications
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>

        {/* Contact Information */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Have questions about your submission?</p>
          <p className="mt-1">
            Contact us at{' '}
            <a href="mailto:support@vibrantcast.com" className="text-purple-600 hover:text-purple-700 underline">
              support@vibrantcast.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubmissionSuccess;
